import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, get, onStepUpRequired, onUnauthorized, post } from "@/api/client";
import type { Me } from "@/api/types";
import { StepUpDialog } from "./StepUpDialog";

type AuthValue = {
  me: Me;
  can: (permission: string) => boolean;
  signOut: () => Promise<void>;
  refresh: () => void;
};

const Ctx = createContext<AuthValue | null>(null);

export const meQueryKey = ["auth", "me"];

export function useMeQuery() {
  return useQuery({
    queryKey: meQueryKey,
    queryFn: () => get<Me>("/auth/me"),
    retry: (count, err) => !(err instanceof ApiError && err.status === 401) && count < 2,
    staleTime: 60_000,
  });
}

export function AuthProvider({ me, children }: { me: Me; children: ReactNode }) {
  const qc = useQueryClient();
  const [stepUp, setStepUp] = useState<((ok: boolean) => void) | null>(null);

  useEffect(() => {
    onStepUpRequired(() => new Promise<boolean>((resolve) => setStepUp(() => resolve)));
    onUnauthorized(() => qc.invalidateQueries({ queryKey: meQueryKey }));
    return () => {
      onStepUpRequired(null);
      onUnauthorized(null);
    };
  }, [qc]);

  const can = useCallback((p: string) => me.permissions.includes(p), [me.permissions]);
  const signOut = useCallback(async () => {
    try {
      await post("/auth/logout");
    } finally {
      qc.clear();
      location.assign("/login");
    }
  }, [qc]);

  const finishStepUp = (ok: boolean) => {
    stepUp?.(ok);
    setStepUp(null);
    if (ok) qc.invalidateQueries({ queryKey: meQueryKey });
  };

  return (
    <Ctx.Provider value={{ me, can, signOut, refresh: () => qc.invalidateQueries({ queryKey: meQueryKey }) }}>
      {children}
      <StepUpDialog open={stepUp !== null} onDone={finishStepUp} />
    </Ctx.Provider>
  );
}

export function useAuth(): AuthValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside AuthProvider");
  return v;
}
